'use client';
/**
 * @file     HousingStockPanelClient.tsx
 * @purpose  The Census housing-stock mix as MeterRows: one row per dwelling
 *           type with its share and count, then the source line.
 */
import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import { MeterRow } from '@/components/hub/MeterRow';
import { hubColors } from '@/components/hub/hubKitStyles';
import { fontWeightTokens } from '@/design-system/tokens/typography';

interface Bucket { label: string; value: number }
interface Props {
  csdName: string | null;
  total: number;
  buckets: Bucket[];
}

export function HousingStockPanelClient({ csdName, total, buckets }: Props) {
  const { t } = useTranslation();
  const sorted = [...buckets].sort((a, b) => b.value - a.value);
  if (total <= 0 || sorted.length === 0) return null;
  return (
    <Box>
      <Box component="h3" sx={{ m: 0, mb: 1, fontSize: 17, fontWeight: fontWeightTokens.semibold, color: hubColors.textPrimary }}>
        {csdName
          ? t('listingDetail.housingStock.titleIn', { defaultValue: 'Homes built in {{place}}, by type', place: csdName })
          : t('listingDetail.housingStock.title', { defaultValue: 'Homes built here, by type' })}
      </Box>
      {sorted.map((b) => {
        const pct = (b.value / total) * 100;
        return (
          <MeterRow
            key={b.label}
            label={b.label}
            value={pct}
            valueText={`${pct.toFixed(1)}% · ${b.value.toLocaleString('en-CA')}`}
            tone="neutral"
          />
        );
      })}
      <Box component="p" sx={{ m: 0, mt: 3, fontSize: 13, color: hubColors.textSecondary }}>
        {t('listingDetail.housingStock.source', {
          defaultValue: 'Source: Statistics Canada, 2021 Census of Population. {{total}} dwellings.',
          total: total.toLocaleString('en-CA'),
        })}
      </Box>
    </Box>
  );
}
